/**
 * PDFs API Client
 *
 * Handles all PDF-related API calls:
 * - Upload PDFs
 * - Extract data from PDFs
 * - Navigate between PDFs
 * - List PDFs in session
 */

import apiClient from './client';
import type {
  PDFUploadResponse,
  PDFExtractionResponse,
  PDFGetResponse,
  PDFListResponse,
} from '../types/api';

export const pdfsApi = {
  /**
   * Upload a PDF file to a session
   */
  uploadPDF: async (
    sessionId: string,
    practiceId: string,
    file: File
  ): Promise<PDFUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/api/v1/pdfs/sessions/${sessionId}/upload`,
      formData,
      {
        params: { practice_id: practiceId },
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  },

  /**
   * Extract data from an uploaded PDF
   */
  extractPDF: async (pdfId: string, customerId: string, reprocess: boolean = false): Promise<PDFExtractionResponse> => {
    const response = await apiClient.post(
      `/api/v1/pdfs/${pdfId}/extract`,
      { reprocess },
      {
        params: { customer_id: customerId },
      }
    );
    return response.data;
  },

  /**
   * Get PDF details and extraction results
   */
  getPDF: async (pdfId: string, customerId: string, includeUrl: boolean = false): Promise<PDFGetResponse> => {
    const response = await apiClient.get(`/api/v1/pdfs/${pdfId}`, {
      params: { customer_id: customerId, include_url: includeUrl },
    });
    return response.data;
  },

  /**
   * List all PDFs in a session
   */
  listPDFs: async (sessionId: string, customerId: string, includeDeleted: boolean = false): Promise<PDFListResponse> => {
    const response = await apiClient.get(`/api/v1/pdfs/sessions/${sessionId}/pdfs`, {
      params: { customer_id: customerId, include_deleted: includeDeleted },
    });
    return response.data;
  },

  /**
   * Navigate to a specific PDF by index in session
   */
  navigateToPDF: async (
    sessionId: string,
    customerId: string,
    pdfIndex: number
  ): Promise<PDFGetResponse> => {
    const response = await apiClient.post(
      `/api/v1/pdfs/sessions/${sessionId}/navigate`,
      {
        pdf_index: pdfIndex,
        include_url: false,
      },
      {
        params: { customer_id: customerId },
      }
    );
    return response.data;
  },

  /**
   * Fetch raw PDF content through the backend proxy (avoids direct blob storage access)
   */
  getPDFContent: async (pdfId: string): Promise<Blob> => {
    const response = await apiClient.get(`/api/v1/pdfs/${pdfId}/content`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Save manual edits to extraction results
   */
  saveExtractionResults: async (
    pdfId: string,
    customerId: string,
    results: PDFExtractionResponse
  ): Promise<PDFExtractionResponse> => {
    const response = await apiClient.post(
      `/api/v1/pdfs/${pdfId}/results`,
      results,
      {
        params: { customer_id: customerId },
      }
    );
    return response.data;
  },

  /**
   * Save parsed widget field edits (auto-save from middle panel)
   */
  saveParsedFields: async (
    pdfId: string,
    customerId: string,
    fields: Record<string, string | boolean | null>
  ): Promise<void> => {
    await apiClient.put(
      `/api/v1/pdfs/${pdfId}/fields`,
      fields,
      {
        params: { customer_id: customerId },
      }
    );
  },

  /**
   * Mark a PDF as viewed (for fair billing)
   */
  markPDFViewed: async (pdfId: string, customerId: string): Promise<any> => {
    const response = await apiClient.post(
      `/api/v1/pdfs/${pdfId}/view`,
      {},
      {
        params: { customer_id: customerId },
      }
    );
    return response.data;
  },
};
